import { InjectionToken } from '@angular/core';
import { PERM } from './app-permissions';
import { MODULE_SETTING } from './module-settings';

/** Aligné sur `AuthService.hasPermission` pour le filtrage du menu. */
export type PermissionMode = 'all' | 'any';

/**
 * Définition partagée : menu latéral (`AppMenu`) et matrice droits par écran (`roles-create`).
 * Les chaînes doivent correspondre exactement aux `name` renvoyés par l’API Spatie.
 */
export interface AppMenuLeafDef {
  label: string;
  icon: string;
  /** Ordre d’affichage (plus petit = plus haut). Si absent, tri après ceux avec ordre. */
  order?: number;
  routerLink?: string;
  target?: '_blank';
  permission?: string | string[];
  permissionMode?: PermissionMode;
  requireSetting?: string;
  /** Droits « actions » (création, modification, gestion…) pour l’éditeur de rôles */
  actionPermissions?: readonly string[];
  /** Entrée sans droit à cocher (ex. tableau de bord ouvert à tous) */
  roleEditorSkip?: boolean;
  /** Réservé aux super-admins uniquement (pas de permission Spatie, rôle seul) */
  superAdminOnly?: boolean;
  /** Visible uniquement si l'utilisateur possède au moins un des rôles listés */
  roles?: string | string[];
}

export interface AppMenuSectionDef {
  /** Libellé groupe dans la sidebar (peut être vide) */
  label: string;
  icon: string;
  /** Ordre d’affichage (plus petit = plus haut). Si absent, tri après ceux avec ordre. */
  order?: number;
  requireSetting?: string;
  /** Titre dans l’accordéon « droits par menu » (si vide, dérivé côté UI) */
  accordionLabel?: string;
  items: readonly AppMenuLeafDef[];
}

export const APP_MENU_SECTIONS: readonly AppMenuSectionDef[] = [
  {
    label: '',
    icon: 'text-xl fa-light fa-gauge',
    accordionLabel: 'Accueil & vente',
    items: [
      {
        label: 'Tableau de bord',
        icon: 'text-xl fa-light fa-gauge',
        routerLink: '/moni/dashboard',
        roleEditorSkip: true,
      },
      {
        label: 'Caisse (POS)',
        icon: 'text-xl fa-light fa-cash-register',
        routerLink: '/pos',
        target: '_blank',
        permission: [PERM.createOrders, PERM.viewOrders],
        permissionMode: 'any',
        requireSetting: MODULE_SETTING.polisi,
      },
    ],
  },
  {
    label: 'Contacts',
    icon: 'text-xl pi pi-box',
    items: [
      {
        label: 'Particuliers',
        icon: 'text-xl fa-light fa-users',
        routerLink: '/moni/clients',
        permission: PERM.viewClients,
        actionPermissions: [PERM.createClients, PERM.updateClients, PERM.deleteClients],
      },
      {
        label: 'Entreprises',
        icon: 'text-xl fa-light fa-building',
        routerLink: '/moni/companies',
        permission: PERM.viewCompanies,
        actionPermissions: [PERM.createCompanies, PERM.updateCompanies, PERM.deleteCompanies],
      },
    ],
  },
  {
    label: 'Achats',
    icon: 'text-xl pi pi-truck',
    items: [
      {
        label: 'Fournisseurs',
        icon: 'text-xl pi pi-truck',
        routerLink: '/moni/fournisseurs',
        permission: PERM.viewSuppliers,
        actionPermissions: [PERM.createSuppliers, PERM.updateSuppliers, PERM.deleteSuppliers],
      },
    ],
  },
  {
    label: 'Catalogue',
    icon: 'text-xl pi pi-box',
    items: [
      {
        label: 'Produits',
        icon: 'text-xl pi pi-box',
        routerLink: '/moni/produits',
        permission: PERM.viewProducts,
        actionPermissions: [PERM.createProducts, PERM.updateProducts, PERM.deleteProducts],
      },
      {
        label: 'Catégories',
        icon: 'text-xl pi pi-tags',
        routerLink: '/moni/categories',
        permission: PERM.viewCategories,
        actionPermissions: [PERM.createCategories, PERM.updateCategories, PERM.deleteCategories],
      },
      {
        label: 'Code Coupons',
        icon: 'text-xl fa-light fa-badge-dollar',
        routerLink: '/moni/reductions',
        permission: PERM.viewReductions,
        actionPermissions: [PERM.createReductions, PERM.updateReductions, PERM.deleteReductions],
      },
    ],
  },
  {
    label: 'Ventes',
    icon: 'text-xl pi pi-shopping-cart',
    items: [
      {
        label: 'Devis',
        icon: 'text-xl pi pi-file-edit',
        routerLink: '/moni/devis',
        permission: PERM.viewQuotes,
        actionPermissions: [PERM.createQuotes, PERM.updateQuotes, PERM.deleteQuotes],
      },
      {
        label: 'Commandes',
        icon: 'text-xl pi pi-shopping-cart',
        routerLink: '/moni/ventes',
        permission: PERM.viewOrders,
        actionPermissions: [PERM.createOrders, PERM.updateOrders, PERM.deleteOrders],
      },
      {
        label: 'Factures',
        icon: 'text-xl pi pi-file',
        routerLink: '/moni/factures',
        permission: PERM.viewInvoices,
        actionPermissions: [PERM.createInvoices, PERM.updateInvoices, PERM.deleteInvoices],
      },
      {
        label: 'Factures recurrentes',
        icon: 'text-xl pi pi-sync',
        routerLink: '/moni/factures-recurrentes',
        permission: PERM.viewInvoices,
        actionPermissions: [PERM.createInvoices, PERM.updateInvoices, PERM.deleteInvoices],
      },
      {
        label: 'Abonnements',
        icon: 'text-xl fa-light fa-rotate',
        routerLink: '/moni/abonnements',
        permission: PERM.viewSubscriptions,
        actionPermissions: [PERM.createSubscriptions, PERM.updateSubscriptions, PERM.deleteSubscriptions],
      },
    ],
  },
  {
    label: 'Comptabilité',
    icon: 'text-xl fa-light fa-calculator',
    requireSetting: MODULE_SETTING.logistiki,
    items: [
      {
        label: 'Dépenses',
        icon: 'text-xl fa-light fa-receipt',
        routerLink: '/moni/depenses',
        permission: PERM.viewExpenses,
        actionPermissions: [PERM.createExpenses, PERM.updateExpenses, PERM.deleteExpenses],
      },
      {
        label: 'Rapprochement bancaire',
        icon: 'text-xl fa-light fa-bank',
        routerLink: '/moni/rapprochement',
        permission: PERM.viewBankReconciliation,
        actionPermissions: [PERM.createBankReconciliation, PERM.updateBankReconciliation, PERM.deleteBankReconciliation],
      },
    ],
  },
  {
    label: 'Paramètres',
    icon: 'text-xl pi pi-cog',
    items: [
      {
        label: 'Taxes / TVA',
        icon: 'text-xl fa-light fa-badge-percent',
        routerLink: '/moni/taxes',
        permission: PERM.viewTaxes,
        actionPermissions: [PERM.createTaxes, PERM.updateTaxes, PERM.deleteTaxes],
      },
      {
        label: 'Types de livraison',
        icon: 'text-xl pi pi-truck',
        routerLink: '/moni/livraisons',
        permission: PERM.viewOptions,
        roleEditorSkip: true,
      },
      {
        label: 'Moyens de paiement',
        icon: 'text-xl pi pi-credit-card',
        routerLink: '/moni/moyens-de-paiement',
        permission: PERM.viewPaymentMethods,
        actionPermissions: [PERM.createPaymentMethods, PERM.updatePaymentMethods, PERM.deletePaymentMethods],
      },
      {
        label: 'Médiathèque',
        icon: 'text-xl pi pi-images',
        routerLink: '/moni/media-library',
        superAdminOnly: true,
      },
    ],
  },
  {
    label: 'Emails',
    icon: 'text-xl pi pi-envelope',
    items: [
      {
        label: 'Test d\'envoi',
        icon: 'text-xl pi pi-send',
        routerLink: '/moni/email-test',
        superAdminOnly: true,
      },
      {
        label: 'Modèles de contenu',
        icon: 'text-xl pi pi-file-edit',
        routerLink: '/moni/email-contents',
        superAdminOnly: true,
      },
      {
        label: 'Template visuel',
        icon: 'text-xl pi pi-palette',
        routerLink: '/moni/email-template',
        superAdminOnly: true,
      },
      {
        label: 'Quota Email',
        icon: 'text-xl pi pi-chart-pie',
        routerLink: '/moni/brevo',
        roleEditorSkip: true,
      },
    ],
  },
  {
    label: 'Administration',
    icon: 'text-xl pi pi-shield',
    items: [
      {
        label: 'Utilisateurs',
        icon: 'text-xl pi pi-user-edit',
        routerLink: '/moni/utilisateurs',
        permission: PERM.viewUsers,
        actionPermissions: [PERM.createUsers, PERM.updateUsers, PERM.assignRoles, PERM.assignPermissions],
      },
      {
        label: 'Rôles & permissions',
        icon: 'text-xl pi pi-lock',
        routerLink: '/moni/roles',
        permission: [PERM.assignRoles, PERM.assignPermissions],
        permissionMode: 'any',
        roleEditorSkip: true,
      },
      {
        label: 'Paramètres généraux',
        icon: 'text-xl fa-light fa-gear',
        routerLink: '/moni/settings',
        roles: ['super-admin', 'Administrateur'],
        permission: PERM.viewSettings,
        actionPermissions: [PERM.createSettings, PERM.updateSettings, PERM.deleteSettings],
      },
      {
        label: 'Groupes de paramètres',
        icon: 'text-xl fa-light fa-gear',
        routerLink: '/moni/settings/groups',
        superAdminOnly: true,
      },
    ],
  },
];

/**
 * Définition du menu injectable pour permettre une surcharge côté `custom`.
 * Par défaut, utilise `APP_MENU_SECTIONS`.
 */
export const APP_MENU_DEFINITION = new InjectionToken<readonly AppMenuSectionDef[]>('APP_MENU_DEFINITION', {
  providedIn: 'root',
  factory: () => APP_MENU_SECTIONS,
});

/**
 * Permissions internes exclues de l'éditeur de rôles (boutique, tarifs, options système…).
 * Elles restent fonctionnelles côté API mais n'ont pas vocation à être assignées manuellement.
 */
export const ROLES_EDITOR_EXCLUDED_PERMS = new Set<string>([
  'view shops', 'create shops', 'update shops', 'delete shops',
  'view product prices', 'manage product prices',
  'view options', 'create options', 'update options', 'delete options',
  'view medias', 'create medias', 'update medias', 'delete medias',
]);

/** Noms de permissions référencés par la structure menu (pour détecter les orphelins côté API). */
export function collectMenuPermissionNames(): Set<string> {
  const set = new Set<string>();
  for (const section of APP_MENU_SECTIONS) {
    for (const item of section.items) {
      const p = item.permission;
      if (p !== undefined) {
        if (Array.isArray(p)) p.forEach((x) => set.add(x));
        else set.add(p);
      }
      item.actionPermissions?.forEach((x) => set.add(x));
    }
  }
  return set;
}
